'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useAppStore } from '@/store/app';
import { api } from '@/lib/api';
import CoinLogo from '@/components/CoinLogo';

interface SectorRotationRow {
  id: string;
  name: string;
  nameZh: string | null;
  topCoins: string[];
  topCoinLogos: string[];
  members: number;
  valid: number;
  coverage: number;
  returnPct: number | null;
  breadthPct: number | null;
  zeroCount: number;
  ranked: boolean;
  note?: string;
}

interface SectorRotationResponse {
  window: string;
  asOf: number;
  historicalRotationAvailable: boolean;
  basis: string;
  sectors: SectorRotationRow[];
}

function LogoImg({ src, symbol }: { src: string; symbol: string }) {
  const [err, setErr] = useState(false);
  if (err) return <CoinLogo symbol={`${symbol}USDT`} size={20} />;
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt="" width={20} height={20} onError={() => setErr(true)} className="h-5 w-5 rounded-full bg-gf-surface2 object-contain" />;
}

/** R02 板块轮动：等权区间收益 + 市场宽度。 */
export default function SectorRotation({ window }: { window: string }) {
  const { language } = useAppStore();
  const zh = language !== 'en';

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['research-sectors', window, language],
    queryFn: () => api.get<SectorRotationResponse>(`/research/sectors?window=${window.toLowerCase()}&lang=${language}`),
    staleTime: 60_000,
    refetchInterval: 300_000,
  });

  const list = data?.sectors ?? [];
  const ranked = list.filter((s) => s.ranked);
  const maxAbs = Math.max(1, ...ranked.map((s) => Math.abs(s.returnPct ?? 0)));
  const winLabel = window === '1D' ? '1D' : window === '30D' ? '30D' : '7D';

  return (
    <div className="card !p-0 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gf-border px-4 py-2.5">
        <div className="text-sm font-semibold">
          {zh ? `板块轮动（${winLabel}）` : `Sector rotation (${winLabel})`}
        </div>
        {data?.historicalRotationAvailable === false && (
          <span className="rounded bg-gf-warn/10 px-2 py-0.5 text-[10px] text-gf-warn">
            {zh ? '历史轮动暂不可用（无历史成员版本）' : 'Historical rotation unavailable (no historical membership)'}
          </span>
        )}
      </div>

      {isLoading && !list.length ? (
        <div className="py-8 text-center text-sm text-gf-muted">{zh ? '加载板块...' : 'Loading sectors...'}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((s) => {
              const isNeg = (s.returnPct ?? 0) < 0;
              const pct = s.returnPct ?? 0;
              const heat = s.ranked ? Math.min(100, (Math.abs(pct) / maxAbs) * 100) : 0;
              return (
                <Link
                  key={s.id}
                  href="/discover"
                  className={`relative flex flex-col gap-1.5 overflow-hidden rounded-lg border px-3 py-2.5 transition-colors hover:bg-gf-surface2/60 ${
                    s.ranked ? 'border-gf-border' : 'border-gf-border/50 opacity-70'
                  }`}
                >
                  {s.ranked && (
                    <span
                      className={`absolute inset-y-0 left-0 w-[3px] ${isNeg ? 'bg-gf-negative/70' : 'bg-gf-positive/70'}`}
                      style={{ opacity: 0.35 + (heat / 100) * 0.65 }}
                    />
                  )}
                  <span className="flex items-center gap-2">
                    <span className="flex w-12 shrink-0">
                      {(s.topCoinLogos.length ? s.topCoinLogos : s.topCoins.map((c) => c))
                        .slice(0, 3)
                        .map((src, i) =>
                          String(src).startsWith('http') ? (
                            <span key={src + i} className={i > 0 ? '-ml-2' : ''}>
                              <LogoImg src={src} symbol={String(s.topCoins[i] ?? '')} />
                            </span>
                          ) : (
                            <span key={src + i} className={i > 0 ? '-ml-2' : ''}>
                              <CoinLogo symbol={`${src}USDT`} size={20} />
                            </span>
                          ),
                        )}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {zh && s.nameZh ? s.nameZh : s.name}
                    </span>
                    <span className={`shrink-0 font-mono text-sm font-semibold ${isNeg ? 'text-gf-negative' : 'text-gf-positive'}`}>
                      {s.returnPct == null ? '—' : `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`}
                    </span>
                  </span>
                  <span className="flex items-center justify-between text-[10px] text-gf-muted">
                    <span>
                      {zh ? '宽度' : 'Breadth'} <span className="font-mono text-gf-text">{s.breadthPct == null ? '—' : `${s.breadthPct.toFixed(1)}%`}</span>
                    </span>
                    <span>
                      {zh ? '有效' : 'Valid'} <span className="font-mono text-gf-text">{s.valid}/{s.members}</span>
                    </span>
                    <span>
                      {zh ? '覆盖' : 'Coverage'} <span className="font-mono text-gf-text">{(s.coverage * 100).toFixed(0)}%</span>
                    </span>
                    {!s.ranked && (
                      <span className="rounded bg-gf-warn/10 px-1 py-0.5 text-[9px] text-gf-warn">
                        {zh ? '不参与排名' : 'Not ranked'}
                      </span>
                    )}
                  </span>
                </Link>
              );
            })}
          </div>
          <p className="border-t border-gf-border/60 px-3 py-2 text-[10px] text-gf-muted">
            {data?.basis ?? ''}
            {data?.asOf ? ` · ${zh ? '更新' : 'Updated'} ${new Date(data.asOf).toLocaleTimeString()}` : ''}
            {isFetching ? (zh ? ' · 刷新中…' : ' · refreshing…') : ''}
          </p>
        </>
      )}
    </div>
  );
}
